import type { SendInfiniteEligibilityRegisterInterestEmailResponse } from '~/services/LuxLoyaltyInfiniteApplicationService';

export type EligibilityRegisterInterestSendRow = {
  id: string;
  registerInterestEmailSent: boolean;
  isRetracted: boolean;
  associatedApplicationCount: number;
  existingMemberAccounts: ReadonlyArray<unknown>;
  existingMemberPending: boolean;
};

export type BulkSendRegisterInterestEmailPlan = {
  ids: Array<string>;
  selectedCount: number;
  alreadySentCount: number;
  retractedCount: number;
  relatedApplicationCount: number;
  unmatchedAccountCount: number;
  pendingMemberLookupCount: number;
};

function hasRelatedApplication(row: EligibilityRegisterInterestSendRow): boolean {
  return row.associatedApplicationCount > 0;
}

function hasMatchedExistingMember(row: EligibilityRegisterInterestSendRow): boolean {
  return !row.existingMemberPending && row.existingMemberAccounts.length > 0;
}

function isOtherwiseSendable(row: EligibilityRegisterInterestSendRow): boolean {
  return !row.registerInterestEmailSent && !row.isRetracted && !hasRelatedApplication(row);
}

export function canSendRegisterInterestEmail(row: EligibilityRegisterInterestSendRow): boolean {
  return isOtherwiseSendable(row) && hasMatchedExistingMember(row);
}

export function getRegisterInterestEmailSendDisabledReason(row: EligibilityRegisterInterestSendRow): string | null {
  if (row.registerInterestEmailSent || row.isRetracted) {
    return null;
  }

  const reasons: Array<string> = [];
  if (row.existingMemberPending) {
    reasons.push('Existing member lookup is still in progress.');
  } else if (row.existingMemberAccounts.length === 0) {
    reasons.push('This user has no matched existing member account.');
  }
  if (hasRelatedApplication(row)) {
    reasons.push('This user has a related application.');
  }

  return reasons.length > 0 ? reasons.join(' ') : null;
}

export function planBulkSendRegisterInterestEmail(
  rows: Array<EligibilityRegisterInterestSendRow>,
): BulkSendRegisterInterestEmailPlan {
  const eligibleRows = rows.filter(canSendRegisterInterestEmail);
  return {
    ids: eligibleRows.map((row) => row.id),
    selectedCount: rows.length,
    alreadySentCount: rows.filter((row) => row.registerInterestEmailSent).length,
    retractedCount: rows.filter((row) => row.isRetracted).length,
    relatedApplicationCount: rows.filter(
      (row) => !row.registerInterestEmailSent && !row.isRetracted && hasRelatedApplication(row),
    ).length,
    unmatchedAccountCount: rows.filter(
      (row) => isOtherwiseSendable(row) && !row.existingMemberPending && row.existingMemberAccounts.length === 0,
    ).length,
    pendingMemberLookupCount: rows.filter((row) => isOtherwiseSendable(row) && row.existingMemberPending).length,
  };
}

export function getBulkSendRegisterInterestEmailEmptyMessage(plan: BulkSendRegisterInterestEmailPlan): string {
  const hasOtherBlocks =
    plan.alreadySentCount > 0 ||
    plan.retractedCount > 0 ||
    plan.relatedApplicationCount > 0 ||
    plan.pendingMemberLookupCount > 0;

  if (plan.unmatchedAccountCount > 0 && !hasOtherBlocks) {
    return plan.selectedCount === 1
      ? 'The selected user has no matched existing member account, so the register interest email cannot be sent.'
      : 'Selected users have no matched existing member account, so the register interest email cannot be sent.';
  }

  const blockedOnlyByRelatedApplication =
    plan.relatedApplicationCount > 0 &&
    plan.alreadySentCount === 0 &&
    plan.retractedCount === 0 &&
    plan.unmatchedAccountCount === 0 &&
    plan.pendingMemberLookupCount === 0;

  if (blockedOnlyByRelatedApplication) {
    return plan.selectedCount === 1
      ? 'The selected user already has a related application, so the register interest email cannot be sent.'
      : 'Selected users already have a related application, so the register interest email cannot be sent.';
  }

  if (
    plan.pendingMemberLookupCount > 0 &&
    plan.alreadySentCount === 0 &&
    plan.retractedCount === 0 &&
    plan.relatedApplicationCount === 0 &&
    plan.unmatchedAccountCount === 0
  ) {
    return 'Existing member lookup is still in progress for the selected users.';
  }

  if (plan.relatedApplicationCount > 0 || plan.unmatchedAccountCount > 0) {
    return 'Selected users have already been sent the register interest email, have been retracted, have a related application, or have no matched existing member account.';
  }

  return 'Selected users have already been sent the register interest email or have been retracted.';
}

export function getBulkSendRegisterInterestEmailConfirmMessage(plan: BulkSendRegisterInterestEmailPlan): string {
  const userLabel = plan.ids.length === 1 ? 'eligible user' : 'eligible users';
  const base = `${plan.ids.length} ${userLabel} will be sent the Infinite register interest email. This can only be sent once per user.`;

  const skipped = [
    plan.relatedApplicationCount === 0
      ? null
      : plan.relatedApplicationCount === 1
        ? '1 selected user already has a related application and will be skipped.'
        : `${plan.relatedApplicationCount} selected users already have a related application and will be skipped.`,
    plan.unmatchedAccountCount === 0
      ? null
      : plan.unmatchedAccountCount === 1
        ? '1 selected user has no matched existing member account and will be skipped.'
        : `${plan.unmatchedAccountCount} selected users have no matched existing member account and will be skipped.`,
  ].filter((part): part is string => part !== null);

  return skipped.length > 0 ? `${base} ${skipped.join(' ')}` : base;
}

export interface BulkSendRegisterInterestEmailResult {
  requestedCount: number;
  sentCount: number;
  skippedCount: number;
  failedCount: number;
}

export function aggregateBulkSendRegisterInterestEmailResults(
  results: Array<PromiseSettledResult<SendInfiniteEligibilityRegisterInterestEmailResponse>>,
): BulkSendRegisterInterestEmailResult {
  return results.reduce<BulkSendRegisterInterestEmailResult>(
    (acc, result) => {
      if (result.status === 'rejected') {
        return { ...acc, failedCount: acc.failedCount + 1 };
      }
      if (result.value.result.outcome === 'sent') {
        return { ...acc, sentCount: acc.sentCount + 1 };
      }
      if (result.value.result.outcome === 'skipped') {
        return { ...acc, skippedCount: acc.skippedCount + 1 };
      }
      return { ...acc, failedCount: acc.failedCount + 1 };
    },
    { requestedCount: results.length, sentCount: 0, skippedCount: 0, failedCount: 0 },
  );
}

export function getBulkSendRegisterInterestEmailResultMessage(result: BulkSendRegisterInterestEmailResult): string {
  const parts = [`${result.sentCount} sent`];
  if (result.skippedCount > 0) {
    parts.push(`${result.skippedCount} skipped`);
  }
  if (result.failedCount > 0) {
    parts.push(`${result.failedCount} failed`);
  }
  const userLabel = result.requestedCount === 1 ? 'eligible user' : 'eligible users';
  return `${result.requestedCount} ${userLabel}: ${parts.join(', ')}.`;
}
